import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Edit, Image as ImageIcon } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as Icons from "lucide-react";


function ImageUploadInput({ label, value, onChange, placeholder, helperText }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("kind", "image");
    
    try {
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onChange(res.data.url);
      toast.success("Imagine incarcata cu succes!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <label className="text-xs text-zinc-500 mb-1 block">{label}</label>}
      <div className="flex gap-2">
        <input className="tf-input py-2 flex-1" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        <label className={`tf-btn-secondary flex items-center justify-center px-4 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <ImageIcon className="w-4 h-4 mr-2" />
          {uploading ? 'Se incarca...' : 'Incarca'}
          <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
        </label>
      </div>
      {helperText && <p className="text-[10px] text-zinc-500 mt-1">{helperText}</p>}
    </div>
  );
}

export default function IDGBoardsTab() {
  const [categories, setCategories] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingBoard, setEditingBoard] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [settingsRes, boardsRes] = await Promise.all([
        api.get("/settings"),
        api.get("/boards")
      ]);
      setCategories(settingsRes.data?.categories || []);
      // boards data includes all boards (root and subboards)
      setBoards(boardsRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveCategories = async (newCategories) => {
    try {
      await api.patch("/admin/settings", { categories: newCategories });
      setCategories(newCategories);
      toast.success("Categorii salvate");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveBoardReorder = async (orderedIds) => {
    try {
      await api.post("/admin/boards/reorder", { ordered_ids: orderedIds });
      toast.success("Ordinea boardurilor salvată");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleCategorySave = (cat) => {
    let next;
    if (categories.find(c => c.id === cat.id)) {
      next = categories.map(c => c.id === cat.id ? cat : c);
    } else {
      next = [...categories, cat];
    }
    saveCategories(next);
    setEditingCategory(null);
  };

  const handleCategoryDelete = (catId) => {
    if (!window.confirm("Sigur vrei să ștergi această categorie? Forumurile vor rămâne, dar e posibil să nu mai fie grupate corect.")) return;
    saveCategories(categories.filter(c => c.id !== catId));
  };

  const handleBoardSave = async (boardData) => {
    try {
      if (boardData.id) {
        await api.patch(`/admin/boards/${boardData.id}`, boardData);
        toast.success("Forum actualizat");
      } else {
        await api.post("/admin/boards", boardData);
        toast.success("Forum creat");
      }
      setEditingBoard(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleBoardDelete = async (boardId) => {
    if (!window.confirm("Ești 100% sigur? TOATE topicurile și postările din acest forum (și sub-forumuri) vor fi ȘTERSE DEFINTIV.")) return;
    try {
      await api.delete(`/admin/boards/${boardId}`);
      toast.success("Forum șters");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-zinc-500 text-sm tf-mono p-4">Loading forums...</div>;

  return (
    <div className="space-y-6" data-testid="idg-boards-tab">
      
      {/* CATEGORIES SECTION */}
      <div className="tf-surface tf-clip-card p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-white/5 pb-3">
          <div>
            <h3 className="tf-heading text-lg font-bold uppercase tracking-widest text-[var(--tf-primary)]">1. Categorii</h3>
            <p className="text-xs text-zinc-500 tf-mono mt-1">Sectiunile mari in care se impart forumurile (ex: GENERAL, CS2, MINECRAFT)</p>
          </div>
          <button onClick={() => setEditingCategory({ id: Math.random().toString(36).substring(2, 10), label: "", color: "#6366f1", icon: "Hash", bg_image: "" })} className="tf-btn-secondary tf-clip-btn text-xs flex items-center gap-1">
            <Plus className="w-3 h-3" /> Categorie Nouă
          </button>
        </div>
        
        <CategoryList items={categories} onChange={saveCategories} onEdit={setEditingCategory} onDelete={handleCategoryDelete} />
      </div>

      {/* BOARDS SECTION */}
      <div className="tf-surface tf-clip-card p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-white/5 pb-3">
          <div>
            <h3 className="tf-heading text-lg font-bold uppercase tracking-widest text-[var(--tf-primary)]">2. Forumuri</h3>
            <p className="text-xs text-zinc-500 tf-mono mt-1">Zonele de discutii in care posteaza userii si sub-forumurile lor</p>
          </div>
          <button onClick={() => setEditingBoard({ name: "", description: "", game: categories[0]?.id || "general", icon: "MessageSquare", parent_id: null, min_role: "user", vip_only: false, background_image_url: "" })} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1">
            <Plus className="w-3 h-3" /> Forum Nou
          </button>
        </div>

        <BoardList boards={boards} categories={categories} onChangeOrder={saveBoardReorder} onEdit={setEditingBoard} onDelete={handleBoardDelete} />
      </div>

      {/* MODALS */}
      {editingCategory && (
        <CategoryModal cat={editingCategory} onSave={handleCategorySave} onClose={() => setEditingCategory(null)} />
      )}
      {editingBoard && (
        <BoardModal board={editingBoard} categories={categories} allBoards={boards} onSave={handleBoardSave} onClose={() => setEditingBoard(null)} />
      )}
    </div>
  );
}

// ----------------- DRAG & DROP FOR CATEGORIES -----------------
function CategoryList({ items, onChange, onEdit, onDelete }) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((x) => x.id === active.id);
    const newIdx = items.findIndex((x) => x.id === over.id);
    onChange(arrayMove(items, oldIdx, newIdx));
  };

  if (!items?.length) return <p className="text-xs tf-mono text-zinc-500 py-4 text-center">Nicio categorie definita.</p>;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((cat, idx) => (
            <CategoryRow key={cat.id} cat={cat} onEdit={() => onEdit(cat)} onDelete={() => onDelete(cat.id)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function CategoryRow({ cat, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  
  let IconCmp = Icons.Hash;
  if (cat.icon && typeof cat.icon === 'string') {
    const exact = Icons[cat.icon];
    const pascal = cat.icon.charAt(0).toUpperCase() + cat.icon.slice(1);
    const pascalMatch = Icons[pascal];
    IconCmp = exact || pascalMatch || Icons.Hash;
  }

  return (
    <div ref={setNodeRef} style={style} className="tf-surface-elev tf-clip-card-sm border border-white/5 p-2 grid grid-cols-12 gap-3 items-center">
      <button {...attributes} {...listeners} className="col-span-1 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-[var(--tf-primary)] flex justify-center">
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="col-span-6 flex items-center gap-3 border-l border-white/10 pl-3">
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || "#fff" }} />
        {(cat.icon && (cat.icon.startsWith('http') || cat.icon.startsWith('/'))) ? (
          <img src={cat.icon} alt="Icon" className="w-4 h-4 object-contain" />
        ) : (
          <IconCmp className="w-4 h-4 text-zinc-400" />
        )}
        <span className="tf-heading font-bold uppercase">{cat.label}</span>
        <span className="text-[10px] tf-mono text-zinc-500 ml-2">({cat.id})</span>
      </div>
      <div className="col-span-3 text-xs text-zinc-400 truncate">
        {cat.bg_image ? <span className="flex items-center gap-1 text-green-400"><ImageIcon className="w-3 h-3" /> Are Bg</span> : "Fara Bg"}
      </div>
      <div className="col-span-2 flex justify-end gap-2">
        <button onClick={onEdit} className="text-zinc-400 hover:text-[var(--tf-primary)] p-1"><Edit className="w-4 h-4" /></button>
        <button onClick={onDelete} className="text-red-500 hover:bg-red-500/10 p-1"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// ----------------- CATEGORY MODAL -----------------
function CategoryModal({ cat, onSave, onClose }) {
  const [form, setForm] = useState(cat);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="tf-surface tf-clip-card p-6 max-w-lg w-full space-y-4">
        <h3 className="tf-section-title">{cat.label ? `Edit Categorie: ${cat.label}` : "Adauga Categorie"}</h3>
        
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">ID Intern (ex: cs2, general, mc)</label>
            <input className="tf-input py-2" value={form.id} onChange={(e) => setForm({...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')})} disabled={!!cat.label} />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Nume Afisat (ex: COUNTER-STRIKE 2)</label>
            <input className="tf-input py-2" value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Culoare Hex (#ff0000)</label>
              <div className="flex gap-2">
                <input type="color" className="w-8 h-8 rounded bg-transparent border-0 p-0 cursor-pointer" value={form.color || "#000000"} onChange={(e) => setForm({...form, color: e.target.value})} />
                <input className="tf-input flex-1 py-1 text-sm" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Lucide Icon (PascalCase, ex: Target)</label>
              <input className="tf-input py-2" value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} />
            </div>
          </div>
          <ImageUploadInput 
              label="Background Image URL (Header Categorie)" 
              value={form.bg_image} 
              onChange={(v) => setForm({...form, bg_image: v})} 
              placeholder="https://..." 
              helperText="Daca lasi liber, se va folosi doar culoarea." 
            />
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Coloane Subforumuri (1-4)</label>
            <select className="tf-input py-2" value={form.subforum_columns || 1} onChange={(e) => setForm({...form, subforum_columns: parseInt(e.target.value)})}>
              <option value={1}>1 Coloană</option>
              <option value={2}>2 Coloane</option>
              <option value={3}>3 Coloane</option>
              <option value={4}>4 Coloane</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t border-white/5 mt-4">
          <button onClick={onClose} className="tf-btn-secondary text-sm px-4 py-1.5">Anulare</button>
          <button onClick={() => onSave(form)} className="tf-btn-primary text-sm px-4 py-1.5">Salveaza</button>
        </div>
      </div>
    </div>
  );
}

// ----------------- DRAG & DROP FOR BOARDS -----------------
function BoardList({ boards, categories, onChangeOrder, onEdit, onDelete }) {
  const validCategoryIds = new Set(categories.map(c => c.id));
  const hasUncategorized = boards.some(b => !b.parent_id && !validCategoryIds.has(b.game || "general"));
  
  const displayTabs = [...categories];
  if (hasUncategorized || categories.length === 0) {
    if (!displayTabs.find(c => c.id === "general")) {
      displayTabs.push({ id: "general", label: categories.length === 0 ? "General" : "Altele (General)", color: "#a1a1aa" });
    }
  }

  const [activeTab, setActiveTab] = useState(displayTabs[0]?.id || "general");
  const [collapsedBoards, setCollapsedBoards] = useState({});

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const toggleCollapse = (id) => {
    setCollapsedBoards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // 1. Group boards by game/category
  // 2. We only render the active category's boards to avoid a huge list
  const activeBoards = boards.filter(b => {
    const game = b.game || "general";
    if (activeTab === "general" && !validCategoryIds.has(game)) return true;
    return game === activeTab;
  });
  
  // Separate into top-level and subs
  const topLevel = activeBoards.filter(b => !b.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    
    // We only allow reordering within the same level (top-level or subs of a specific board)
    const isTopLevel = topLevel.find(b => b.id === active.id);
    let listToReorder = isTopLevel 
      ? topLevel 
      : activeBoards.filter(b => b.parent_id === activeBoards.find(x => x.id === active.id)?.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
      
    const oldIdx = listToReorder.findIndex((x) => x.id === active.id);
    const newIdx = listToReorder.findIndex((x) => x.id === over.id);
    
    if (oldIdx !== -1 && newIdx !== -1) {
      const reorderedList = arrayMove(listToReorder, oldIdx, newIdx);
      // Construct a new full ordered list based on this local change
      // Just map everything else as is, and replace this slice
      const otherBoards = boards.filter(b => !reorderedList.find(r => r.id === b.id));
      const fullNewOrder = [...otherBoards, ...reorderedList];
      // Note: This simple backend reorder expects a flat array of IDs, we send it all IDs
      onChangeOrder(fullNewOrder.map(b => b.id));
    }
  };

  if (!boards?.length) return <p className="text-xs tf-mono text-zinc-500 py-4 text-center">Niciun board creat.</p>;

  return (
    <div className="space-y-4">
      {/* Category Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {displayTabs.map(cat => (
          <button
            key={cat.id}
            onClick={() => setActiveTab(cat.id)}
            className={`shrink-0 px-3 py-1.5 text-[10px] tf-heading font-bold uppercase tracking-widest transition-all tf-clip-card-sm border ${activeTab === cat.id ? "text-white border-current" : "text-zinc-500 border-transparent hover:text-zinc-300 hover:bg-white/5"}`}
            style={activeTab === cat.id ? { color: cat.color || "#fff", borderColor: cat.color || "#fff", backgroundColor: (cat.color || "#fff") + "15" } : {}}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="space-y-1.5">
          {topLevel.length === 0 && <p className="text-xs tf-mono text-zinc-500 py-4 text-center">Niciun board in aceasta categorie.</p>}
          <SortableContext items={topLevel.map(x => x.id)} strategy={verticalListSortingStrategy}>
            {topLevel.map((board) => {
              const subs = activeBoards.filter(b => b.parent_id === board.id).sort((a, b) => (a.order || 0) - (b.order || 0));
              const isCollapsed = collapsedBoards[board.id];
              return (
                <div key={board.id} className="space-y-1">
                  <BoardRow 
                    board={board} 
                    isChild={false}
                    hasChildren={subs.length > 0}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={() => toggleCollapse(board.id)}
                    onEdit={() => onEdit(board)} 
                    onDelete={() => onDelete(board.id)} 
                  />
                  
                  {!isCollapsed && subs.length > 0 && (
                    <SortableContext items={subs.map(x => x.id)} strategy={verticalListSortingStrategy}>
                      <div className="pl-6 space-y-1 mt-1 border-l-2 border-white/5 ml-3">
                        {subs.map(sub => (
                          <BoardRow 
                            key={sub.id} 
                            board={sub} 
                            isChild={true}
                            onEdit={() => onEdit(sub)} 
                            onDelete={() => onDelete(sub.id)} 
                          />
                        ))}
                      </div>
                    </SortableContext>
                  )}
                </div>
              );
            })}
          </SortableContext>
        </div>
      </DndContext>
    </div>
  );
}

function BoardRow({ board, isChild, hasChildren, isCollapsed, onToggleCollapse, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: board.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 1 };
  
  // Safe icon resolution
  let IconCmp = Icons.MessageSquare;
  if (board.icon && typeof board.icon === 'string') {
    const exactMatch = Icons[board.icon];
    const pascal = board.icon.charAt(0).toUpperCase() + board.icon.slice(1);
    const pascalMatch = Icons[pascal];
    IconCmp = exactMatch || pascalMatch || Icons.MessageSquare;
  }

  return (
    <div ref={setNodeRef} style={style} className={`tf-surface-elev tf-clip-card-sm border border-white/5 p-2.5 flex items-center gap-3 transition-colors hover:bg-white/[0.03] ${isDragging ? 'shadow-xl shadow-black border-white/20' : ''}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-[var(--tf-primary)] flex justify-center shrink-0">
        <GripVertical className="w-4 h-4" />
      </button>

      {hasChildren && !isChild && (
        <button onClick={onToggleCollapse} className="text-zinc-500 hover:text-white shrink-0 transition-transform" style={{ transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)" }}>
          <Icons.ChevronRight className="w-4 h-4" />
        </button>
      )}
      
      {!hasChildren && !isChild && <div className="w-4 shrink-0" />}

      <div className="flex items-center justify-center w-8 h-8 rounded bg-white/5 shrink-0 border border-white/5">
        {(board.icon && (board.icon.startsWith('http') || board.icon.startsWith('/'))) ? (
          <img src={board.icon} alt="Icon" className="w-4 h-4 object-contain" />
        ) : (
          <IconCmp className="w-4 h-4 text-zinc-400" />
        )}
      </div>

      <div className="flex-1 min-w-0 flex items-center justify-between">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className="tf-heading font-bold text-white uppercase text-sm truncate">{board.name}</span>
            {board.vip_only && <span className="text-[9px] font-bold text-yellow-500 bg-yellow-500/10 px-1 py-0.5 rounded uppercase">VIP</span>}
            {board.min_role !== "user" && <span className="text-[9px] font-bold text-red-400 bg-red-400/10 px-1 py-0.5 rounded uppercase">{board.min_role}</span>}
            {board.background_image_url && <span className="text-[9px] font-bold text-green-400 bg-green-400/10 px-1 py-0.5 rounded uppercase flex items-center gap-1"><ImageIcon className="w-3 h-3" /> BG</span>}
          </div>
          <div className="text-[10px] text-zinc-500 tf-mono truncate mt-0.5">{board.description || "Fără descriere"}</div>
        </div>
      </div>

      <div className="flex justify-end gap-1.5 shrink-0">
        <button onClick={onEdit} className="text-zinc-400 hover:text-[var(--tf-primary)] p-1.5 bg-white/5 hover:bg-[var(--tf-primary)]/10 rounded transition-colors"><Edit className="w-3.5 h-3.5" /></button>
        <button onClick={onDelete} className="text-red-500/70 hover:text-red-500 p-1.5 bg-white/5 hover:bg-red-500/10 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// ----------------- BOARD MODAL -----------------
function BoardModal({ board, categories, allBoards, onSave, onClose }) {
  const [form, setForm] = useState({
    id: board.id,
    name: board.name || "",
    description: board.description || "",
    game: board.game || (categories[0]?.id || "general"),
    icon: board.icon || "MessageSquare",
    parent_id: board.parent_id || null,
    min_role: board.min_role || "user",
    vip_only: board.vip_only || false,
    background_image_url: board.background_image_url || "",
    subforum_columns: board.subforum_columns || "",
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="tf-surface tf-clip-card p-6 max-w-2xl w-full space-y-4 my-8">
        <h3 className="tf-section-title">{board.id ? `Edit Forum: ${board.name}` : "Adauga Forum Nou"}</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-xs text-zinc-500 mb-1 block">Nume Forum</label>
            <input className="tf-input py-2" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
          </div>
          
          <div className="col-span-2">
            <label className="text-xs text-zinc-500 mb-1 block">Descriere</label>
            <textarea className="tf-input py-2" rows="2" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Categorie (Grupajul principal)</label>
            <select className="tf-input py-2" value={form.game} onChange={(e) => setForm({...form, game: e.target.value})}>
              {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Parent Board (pentru sub-forumuri)</label>
            <select className="tf-input py-2" value={form.parent_id || ""} onChange={(e) => setForm({...form, parent_id: e.target.value || null})}>
              <option value="">-- Root Board (Niciun parinte) --</option>
              {allBoards.filter(b => b.id !== board.id).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          <ImageUploadInput 
              label="Lucide Icon sau Imagine URL" 
              value={form.icon} 
              onChange={(v) => setForm({...form, icon: v})} 
            />

          <ImageUploadInput 
              label="Background Image URL (Imagine Fundal Board)" 
              value={form.background_image_url} 
              onChange={(v) => setForm({...form, background_image_url: v})} 
              placeholder="https://..." 
            />

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Rol Minim</label>
            <select className="tf-input py-2" value={form.min_role} onChange={(e) => setForm({...form, min_role: e.target.value})}>
              <option value="guest">Guest</option>
              <option value="user">User</option>
              <option value="mod">Moderator</option>
              <option value="admin">Administrator</option>
            </select>
          </div>

          {!form.parent_id && (
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Coloane Subforumuri (1-4)</label>
              <select className="tf-input py-2" value={form.subforum_columns || ""} onChange={(e) => setForm({...form, subforum_columns: e.target.value ? parseInt(e.target.value) : null})}>
                <option value="">-- Implicit Categorie --</option>
                <option value={1}>1 Coloană</option>
                <option value={2}>2 Coloane</option>
                <option value={3}>3 Coloane</option>
                <option value={4}>4 Coloane</option>
              </select>
            </div>
          )}

          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" id="vip_only" checked={form.vip_only} onChange={(e) => setForm({...form, vip_only: e.target.checked})} className="w-4 h-4 accent-[var(--tf-primary)]" />
            <label htmlFor="vip_only" className="text-sm font-bold text-yellow-500">Doar pentru VIP?</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-5 border-t border-white/5 mt-4">
          <button onClick={onClose} className="tf-btn-secondary text-sm px-4 py-1.5">Anulare</button>
          <button onClick={() => {
            const copy = {...form};
            if (!copy.parent_id) copy.parent_id = null; // Important!
            if (copy.subforum_columns === "") copy.subforum_columns = null;
            onSave(copy);
          }} className="tf-btn-primary text-sm px-4 py-1.5">Salveaza</button>
        </div>
      </div>
    </div>
  );
}
